"use client";

import { Icon, type IconName } from "../Icon";

export interface SearchEmptyStateProps {
  /**
   * Message to display
   * @default "No results found"
   */
  message?: string;
  /**
   * Optional suggestion text
   */
  suggestion?: string;
  /**
   * Icon to display
   * @default "MagnifyingGlass"
   */
  icon?: IconName;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * SearchEmptyState
 *
 * Empty state displayed when search returns no results.
 * Shows a helpful message and optional suggestions.
 *
 * @example
 * ```tsx
 * <SearchEmptyState
 *   message="No sites found"
 *   suggestion="Try searching for a different location"
 * />
 * ```
 */
export function SearchEmptyState({
  message = "No results found",
  suggestion,
  icon = "MagnifyingGlass",
  className = "",
}: SearchEmptyStateProps) {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}>
      <div className="text-text-muted mb-3">
        <Icon name={icon} size={48} />
      </div>
      <p className="text-base font-medium text-text-body mb-1">{message}</p>
      {suggestion && <p className="text-sm text-text-muted max-w-xs">{suggestion}</p>}
    </div>
  );
}
